import { useEffect, useRef } from 'react';

const POLL_INTERVAL_MS = 2000;

/**
 * Polls `tick` every 2s while `inFlight` is true — the only way this page learns that a relance's stage
 * moved, since nothing pushes that to the client (see event-flow-and-patterns.md § the pattern this
 * story introduces: a stage map read on demand, not a stream).
 *
 * Stops on unmount, the moment `inFlight` flips false, and on a rejected tick — a 404 mid-poll means the
 * relance was removed concurrently, which the page's own terminal-snapshot re-read already handles with
 * its toast and redirect, so a poll tick just goes quiet rather than duplicating that.
 *
 * `tick` is read through a ref so callers can pass a fresh closure every render (as the plan's call site
 * does) without restarting the interval on every unrelated re-render — only `inFlight` does that.
 */
export function useInFlightPoll(inFlight: boolean, tick: () => Promise<unknown>): void {
    const tickRef = useRef(tick);
    tickRef.current = tick;

    useEffect(() => {
        if (!inFlight) return;

        let stopped = false;
        const id = setInterval(() => {
            void tickRef.current().catch(() => {
                if (!stopped) {
                    stopped = true;
                    clearInterval(id);
                }
            });
        }, POLL_INTERVAL_MS);

        return () => {
            stopped = true;
            clearInterval(id);
        };
    }, [inFlight]);
}
